import { type ReactNode } from 'react';
import { twJoin } from 'tailwind-merge';

import { Span } from '@/core/tamagoshiTailwind/components/Typography/Typography';
import { AlertIcon } from '@/core/tamagoshiTailwind/icons/AlertIcon';
import { CheckCircleFullIcon } from '@/core/tamagoshiTailwind/icons/CheckCircleFullIcon';
import { InfoFullIcon } from '@/core/tamagoshiTailwind/icons/InfoFullIcon';
import { WarningFullIcon } from '@/core/tamagoshiTailwind/icons/WarningFullIcon';

export const VARIANTS = {
  Information: {
    className: 'bg-info-primary text-foundation-primary',
    icon: <InfoFullIcon className="size-[16px] text-info-primary" />,
  },
  Success: {
    className: 'bg-success-primary text-foundation-primary',
    icon: <CheckCircleFullIcon className="size-[16px] text-success-primary" />,
  },
  Warning: {
    className: 'bg-warning-primary text-foundation-primary',
    icon: <WarningFullIcon className="size-[16px] text-warning-primary" />,
  },
  Error: {
    className: 'bg-error-secondary text-foundation-primary',
    icon: <AlertIcon className="size-[16px] text-error-primary" />,
  },
} as const satisfies Record<string, { className: string; icon: ReactNode }>;

export type TagProps = {
  showIcon?: boolean;
  text: string;
  variant: keyof typeof VARIANTS;
};

/**
 * An inline semantic tag.
 *
 * Decorated with an icon depending on the provided variant.
 *
 * See the related [Figma][figma] for more details.
 *
 * [figma]: https://www.figma.com/design/KAiRlBccr6bFYmKby9wQrR?node-id=15439-3562
 */
export const Tag = ({ showIcon, text, variant }: TagProps) => {
  const { className, icon } = VARIANTS[variant];
  return (
    <span
      className={twJoin(
        `inline-flex h-[28px] w-fit items-center justify-center gap-xs
        rounded-tag px-s desktop:text-small`,
        className,
      )}
    >
      {showIcon && icon}
      <Span scale="body2" weight="semibold" className="line-clamp-1">
        {text}
      </Span>
    </span>
  );
};

Tag.displayName = 'Tag';
